import React, { useEffect, useState, createContext } from 'react'
import { PlayerURLs } from './PlayerURLs'
import { PlayerProfileUrlParams, usePlayerUrlParams } from '../utils'
import {
  EventGQL,
  ExtraInfo,
  FeedItem,
  GameLog,
  PlayerCareerStats,
  PlayerDataGQL,
  PlayersStatsData,
  fetchPlayerProfileBySlugGQL,
  updatePlayerSeasonStats,
  TeamFrontData,
  fetchPlayerEventStats,
  getFeedByTag,
} from '../../Api'
import {
  CURRENT_YEAR,
  EVENTS_YEAR,
  STATS_YEAR,
  YEAR_MIN,
  champSeriesDateRanges,
} from '../../Config/League/league.config'
import { eventParse } from './eventParse'
import type { Event } from './eventParse'
import type { StatTypes, FilteredResult } from '../../Constant/statsProcess'
import { convertAllEvents } from '../../Context/utils'
import type { SeasonType } from '../../Routing/urls'
import {
  getTeamEvents,
  getAdditionalTeamData,
  extractStatFromObject,
  SEASON_SEGMENT_NAMES,
} from '../../Utils'
import { websiteUrls } from '../../Constant'

export type GameLogExt = {
  raw: GameLog
  values: FilteredResult[]
  event: Event
  eventUrl: string
}

export type Season = { seasonType: SeasonType; year: number }
export type SeasonStatArrays = {
  regular: PlayersStatsData | null
  post: PlayersStatsData | null
  champseries: PlayersStatsData | null
}

type PlayerProfileContextType = {
  params: PlayerProfileUrlParams | null
  infoLoaded: boolean
  statsLoaded: boolean
  error: Error | null
  player: PlayerDataGQL | null
  seasonStatsArrays: SeasonStatArrays
  seasonStats: PlayersStatsData | null
  career: PlayerCareerStats | null
  gameLogs: GameLog[]
  playerEvents: EventGQL[]
  teamMates: PlayerDataGQL[]
  media: FeedItem[]
  extraInfo: ExtraInfo | null
  team: TeamFrontData | null
  seasonSelect: Season
  urls: PlayerURLs | null
  getStats: (
    statType: StatTypes,
    useChampSeries: boolean,
    stats: PlayersStatsData | PlayerCareerStats
  ) => FilteredResult[]
  onStatsDropdownChange: (seasonSelect: Season) => void
  getPlayerLeague: (name: string) => string
  getGameLogs: () => GameLogExt[]
}

export const PlayerProfileContext = createContext<PlayerProfileContextType | null>(null)

export const PlayerProfileProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const params = usePlayerUrlParams()
  const [playerId, setPlayerId] = useState<string>('')
  const [infoLoaded, setInfoLoaded] = useState<boolean>(false)
  const [statsLoaded, setStatsLoaded] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [player, setPlayer] = useState<PlayerDataGQL | null>(null)
  const [seasonStats, setSeasonStats] = useState<PlayersStatsData | null>(null)
  const [seasonStatsArrays, setSeasonStatsArrays] = useState<SeasonStatArrays>({
    regular: null,
    post: null,
    champseries: null,
  })
  const [career, setCareer] = useState<PlayerCareerStats | null>(null)
  const [gameLogs, setGameLogs] = useState<GameLog[]>([])
  const [playerEvents, setPlayerEvents] = useState<EventGQL[]>([])
  const [teamMates, setTeamMates] = useState<PlayerDataGQL[]>([])
  const [media, setMedia] = useState<FeedItem[]>([])
  const [extraInfo, setExtraInfo] = useState<ExtraInfo | null>(null)
  const [team, setTeam] = useState<TeamFrontData | null>(null)
  const [seasonSelect, setSeasonSelect] = useState<Season>({
    seasonType: SEASON_SEGMENT_NAMES.champseries,
    year: CURRENT_YEAR,
  })
  const [urls, setUrls] = useState<PlayerURLs | null>(null)

  useEffect(() => {
    if (!params?.slug) {
      console.log('No slug')
      return
    }
    setSeasonSelect({
      seasonType: params?.type ? params?.type : seasonSelect.seasonType,
      //Wordpress can't handle year as param
      year: params.yr ? params.yr : params?.year ? params.year : CURRENT_YEAR,
    })
    setPlayerId(params.slug)
    fetchData()
  }, [])

  const fetchData = async () => {
    if (!params) {
      console.log('No slug')
      return
    }
    let prms = {
      slug: params.slug,
      tab: params.tab,
      year: params.year || seasonSelect.year,
      statYear: STATS_YEAR,
      type: params.type || seasonSelect.seasonType,
      eventsYear: params.eventsYear,
    }
    try {
      await Promise.all([fetchProfileGQL(prms), fetchPlayerFeed(prms.slug, true, 10)])
    } catch (err: any) {
      console.log(err)
      setError(err)
    }
    return
  }

  const fetchPlayerFeed = async (
    slug: string,
    includeVideos: boolean = false,
    limit?: number
  ) => {
    const feedParams = {
      tags: [slug],
      includeVideos,
      limit,
    }

    // If feed is already feched, don't fetch again
    if (media.length > 0) return
    try {
      const feed = await getFeedByTag(feedParams)
      if (!feed) return []
      setMedia(feed)
      return feed.length > 0 ? feed : []
    } catch (err) {
      console.log(err)
      return null
    }
  }

  const fetchProfileGQL = async (prms: PlayerProfileUrlParams) => {
    let infoYear = params?.year ?? CURRENT_YEAR
    const playerData = await fetchPlayerProfileBySlugGQL(
      prms.slug,
      infoYear,
      prms.statYear ?? prms.year,
      prms.eventsYear ?? EVENTS_YEAR
    )
    if (!playerData) return
    let urls = new PlayerURLs(playerData, seasonSelect)

    // Sort all years array, sometimes unsorted. Need to remove filter when displaying MLL stats
    playerData.allYears = playerData.allYears
      .filter((year) => year >= YEAR_MIN)
      .sort((a, b) => a - b)

    let determinedSel = determineSeasonSelector(playerData, playerData.allYears)
    setSeasonSelect(determinedSel)
    setUrls(urls)
    setPlayer(playerData)
    setCareer(playerData.careerStats)
    setExtraInfo(playerData.extraInfo)
    assignPlayerTeammates(playerData)
    getTeam(playerData)
    setInfoLoaded(true)

    //Fetch Stats for year
    await Promise.all([
      fetchPlayerStats(playerData.slug, determinedSel),
      fetchPlayerGameStats(playerData, determinedSel),
    ])
  }

  const determineSeasonSelector = (
    player: PlayerDataGQL,
    allYears: number[]
  ): Season => {
    const now = Math.floor(Date.now() / 1000)
    const csRange = champSeriesDateRanges[CURRENT_YEAR]
    if (now >= csRange.startTime && now <= csRange.endTime) {
      //Champ series is live, not participating
      if (!player.champSeries) return getSelectorByLastYear(allYears)

      //Champ series is live & player is participating
      return {
        seasonType: SEASON_SEGMENT_NAMES.champseries,
        year: CURRENT_YEAR,
      }
    } else {
      return getSelectorByLastYear(allYears)
    }
  }

  const getSelectorByLastYear = (allYears: number[]) => {
    //Check if player has stats for current year
    let sel: Season
    const latestYear = allYears[allYears.length - 1]
    if (allYears.length > 0 && latestYear < CURRENT_YEAR) {
      sel = {
        seasonType: SEASON_SEGMENT_NAMES.regular,
        year: latestYear,
      }
    } else {
      sel = {
        seasonType: SEASON_SEGMENT_NAMES.regular,
        year: CURRENT_YEAR,
      }
    }
    return sel
  }

  const fetchPlayerStats = async (slug: string, seasonOpts: Season) => {
    let pStatsRes = await updatePlayerSeasonStats(
      seasonOpts.year,
      slug,
      seasonOpts.year,
      seasonOpts.seasonType,
      seasonOpts.year
    )
    if (!pStatsRes?.player) return
    updateStatsInState(seasonOpts, pStatsRes.player)
  }

  const fetchPlayerGameStats = async (player: PlayerDataGQL, seasonOpts: Season) => {
    const includeCS = seasonOpts.seasonType === SEASON_SEGMENT_NAMES.champseries
    let pStatsRes = await fetchPlayerEventStats(seasonOpts.year, player.slug, includeCS)
    if (!pStatsRes?.player?.allEvents) return

    let plr = { ...player, allEvents: pStatsRes.player.allEvents }

    updateGameStatsInState(plr, seasonOpts.year)
    assignTeamEvents(plr)
  }

  const assignPlayerTeammates = (player: PlayerDataGQL) => {
    if (!player || !player.currentTeam) return
    const players = player.currentTeam.players
    if (players) {
      let tms = players.filter(
        (p) =>
          p.officialId !== player.officialId &&
          p.position === player.currentTeam.position
      )
      setTeamMates(tms)
    }
  }

  const assignTeamEvents = (player: PlayerDataGQL) => {
    if (!player?.currentTeam) return
    try {
      if (player.allEvents) {
        const evs = getTeamEvents(player.allEvents, player.currentTeam.officialId)
        setPlayerEvents(evs)
      }
    } catch (err) {
      console.log(err)
    }
  }

  /** Get team data for player */
  const getTeam = (plr: PlayerDataGQL) => {
    if (plr?.currentTeam) {
      let team = getAdditionalTeamData(plr.currentTeam.officialId)
      setTeam(team)
      return
    } else if (plr?.champSeries?.officialId) {
      let team = getAdditionalTeamData(plr.champSeries?.officialId)
      setTeam(team)
      return team
    } else {
      return null
    }
  }

  const updateStatsInState = (sSelect: Season, plr: PlayerDataGQL) => {
    const statArrays = {
      regular: plr.stats,
      post: plr.postStats,
      champseries: plr.champSeries?.stats || null,
    }
    setSeasonStatsArrays(statArrays)
    updateSeasonStatsList(sSelect, statArrays)
  }

  const updateGameStatsInState = (player: PlayerDataGQL, year: number) => {
    if (player?.allEvents && player.allEvents.length > 0) {
      //TODO: find permanent fix for traded players
      const playerYearTeam = player.allSeasonStats?.findLast(
        (season) => season.year === year
      )?.teamId
      const logs = convertAllEvents(
        player,
        player.allEvents,
        player.teams,
        playerYearTeam
      )
      setGameLogs(logs)
    }
  }

  const updateSeasonStatsList = (sSelect: Season, statArrayList: SeasonStatArrays) => {
    let arrayKey = sSelect.seasonType as keyof SeasonStatArrays

    if (statArrayList[arrayKey]) {
      setSeasonStats(statArrayList[arrayKey])
      setStatsLoaded(true)
      return
    }
  }

  /**
   * Returns stats for player depending at his position and stat type
   */
  const getStats = (
    statType: StatTypes,
    useChampSeries: boolean,
    statsObj: PlayersStatsData | PlayerCareerStats
  ): FilteredResult[] => {
    let pos = useChampSeries
      ? player?.champSeries?.position
      : player?.currentTeam?.position
    if (!pos) {
      pos = 'A'
    }
    return extractStatFromObject(pos, statType, statsObj)
  }

  /**
   * Get processed game logs
   */
  const getGameLogs = (): GameLogExt[] => {
    const pos = player?.currentTeam?.position
    if (!pos) return []
    if (gameLogs.length < 1) return []
    return gameLogs
      .sort((a, b) => a.startTime - b.startTime)
      .map((log) => {
        const event = eventParse(log)
        if (event === null) return null
        const values = extractStatFromObject(pos, 'game', log)
        return {
          raw: log,
          values: values,
          event: event,
          eventUrl: `${websiteUrls.statsBaseUrl}/games/${seasonSelect.year}/${log.eventId}`,
        }
      })
      .filter(
        (log) => log !== null && log.raw.seasonSegment === seasonSelect.seasonType
      ) as GameLogExt[]
  }

  const getPlayerLeague = (name: string): string => {
    const league =
      name === 'stats'
        ? seasonStatsArrays['regular']?.statLeague
        : player?.currentTeam.league
    if (!league) return 'PLL'
    return league
  }

  const onStatsDropdownChange = async (seasonOpts: Season) => {
    setStatsLoaded(false)
    if (!playerId || !player) return
    if (seasonSelect.year !== seasonOpts.year) {
      //Year changed, fetch new stats
      setSeasonSelect(seasonOpts)
      await Promise.all([
        fetchPlayerStats(player.slug, seasonOpts),
        fetchPlayerGameStats(player, seasonOpts),
      ])
    } else {
      //Only change seasonStats
      setSeasonSelect(seasonOpts)
      updateSeasonStatsList(seasonOpts, seasonStatsArrays)
      setStatsLoaded(true)
    }
  }

  return (
    <PlayerProfileContext.Provider
      value={{
        params,
        infoLoaded,
        statsLoaded,
        error,
        player,
        seasonStats,
        seasonStatsArrays,
        career,
        gameLogs,
        playerEvents,
        teamMates,
        media,
        extraInfo,
        team,
        seasonSelect,
        urls,
        getStats,
        onStatsDropdownChange,
        getPlayerLeague,
        getGameLogs,
      }}
    >
      {children}
    </PlayerProfileContext.Provider>
  )
}
